<template>
  <button
    type="button"
    class="tt"
    :class="{ 'tt--night': dark, 'tt--mini': mini }"
    role="switch"
    :aria-checked="dark"
    :aria-label="label"
    :title="label"
    @click="toggle"
  >
    <span class="tt-sky tt-sky--day"></span>
    <span class="tt-sky tt-sky--night"></span>

    <!-- gece: yildizlar -->
    <span class="tt-stars" aria-hidden="true">
      <i v-for="(s, i) in STARS" :key="i" :style="{ left: s[0] + '%', top: s[1] + '%', '--d': s[2] + 's', '--s': s[3] + 'px' }"></i>
    </span>

    <!-- gunduz: sag tarafta sari gunes / gece: solda hilal -->
    <span class="tt-sun" aria-hidden="true"></span>
    <span class="tt-moon" aria-hidden="true"></span>

    <!-- bulutlar (iki katman, yavasca kayar) -->
    <svg class="tt-clouds tt-clouds--back" viewBox="0 0 240 40" preserveAspectRatio="none" aria-hidden="true">
      <g class="tt-drift">
        <path :d="CLOUD" />
        <path :d="CLOUD" transform="translate(240 0)" />
      </g>
    </svg>
    <svg class="tt-clouds tt-clouds--front" viewBox="0 0 240 40" preserveAspectRatio="none" aria-hidden="true">
      <g class="tt-drift tt-drift--fast">
        <path :d="CLOUD2" />
        <path :d="CLOUD2" transform="translate(240 0)" />
      </g>
    </svg>

    <!-- kaydirma topu: gunduz solda beyaz gunes, gece sagda parlayan dolunay -->
    <span class="tt-knob" aria-hidden="true"><span class="tt-craters"><i></i><i></i><i></i></span></span>
  </button>
</template>

<script setup lang="ts">
// Gece / gunduz dugmesi (09.10.2026). Sola = gunduz, saga = gece.
// Sitenin mevcut tema mantigiyla ayni: <html class="dark"> + localStorage 'color-theme'.
// Tema baska yerden (Настройки) degisirse MutationObserver ile dugme de kendini gunceller.
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'

const props = defineProps<{ mini?: boolean }>()
const mini = computed(() => !!props.mini)
const dark = ref(false)

const STARS: [number, number, number, number][] = [
  [12, 22, 0, 2], [24, 58, 1.2, 1.5], [33, 18, 0.6, 2], [41, 46, 2.1, 1.5], [52, 24, 1.6, 2.5],
  [60, 62, 0.3, 1.5], [68, 16, 2.4, 2], [18, 70, 1.8, 1.5], [47, 74, 0.9, 1.5], [29, 36, 2.8, 1.5],
]
const CLOUD =
  'M0 40 V26 C4 14 18 12 24 20 C28 6 46 4 52 16 C58 8 72 10 74 20 C82 12 96 14 98 24 C104 10 124 8 128 20 C134 6 152 6 156 18 C162 10 176 12 178 22 C186 12 202 14 204 24 C210 12 228 12 232 22 C236 20 240 22 240 26 V40 Z'
const CLOUD2 =
  'M0 40 V32 C6 22 20 22 24 30 C30 18 48 18 52 28 C58 20 72 22 74 30 C82 20 98 20 102 30 C108 22 122 22 126 30 C132 18 150 18 154 28 C160 22 174 22 178 31 C186 22 200 22 204 30 C210 22 226 22 230 31 C234 29 240 30 240 32 V40 Z'

const label = computed(() => (dark.value ? 'Ночной режим' : 'Дневной режим'))

function apply(isDark: boolean) {
  document.documentElement.classList.toggle('dark', isDark)
  try {
    localStorage.setItem('color-theme', isDark ? 'dark' : 'light')
  } catch {
    /* depolama kapaliysa sessiz devam */
  }
}
function toggle() {
  dark.value = !dark.value
  apply(dark.value)
}

let obs: MutationObserver | null = null
onMounted(() => {
  dark.value = document.documentElement.classList.contains('dark')
  obs = new MutationObserver(() => {
    dark.value = document.documentElement.classList.contains('dark')
  })
  obs.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] })
})
onBeforeUnmount(() => obs?.disconnect())
</script>

<style>
.tt {
  --k: 40px; /* top boyutu */
  position: relative; display: block; width: 100%; height: 50px; padding: 0; border: 0; cursor: pointer;
  border-radius: 999px; overflow: hidden; isolation: isolate; -webkit-tap-highlight-color: transparent;
  box-shadow: inset 0 3px 8px rgba(0, 0, 0, 0.28), inset 0 -2px 4px rgba(255, 255, 255, 0.25), 0 6px 18px -8px rgba(30, 80, 140, 0.55);
  transition: box-shadow 0.6s;
}
.tt:focus-visible { outline: 2px solid #3dd6a3; outline-offset: 3px; }
.tt--night { box-shadow: inset 0 3px 8px rgba(0, 0, 0, 0.5), inset 0 -2px 4px rgba(140, 160, 255, 0.15), 0 6px 20px -8px rgba(80, 90, 220, 0.6); }
.tt-sky { position: absolute; inset: 0; transition: opacity 0.7s ease; }
.tt-sky--day { background: radial-gradient(120% 140% at 85% 30%, #bfe6ff 0%, transparent 55%), linear-gradient(180deg, #3d8fd8 0%, #6db8f2 60%, #9fd4f7 100%); }
.tt-sky--night { opacity: 0; background: radial-gradient(90% 140% at 20% 30%, rgba(120, 110, 255, 0.35), transparent 60%), linear-gradient(180deg, #0b1236 0%, #1b2363 60%, #2b2f7a 100%); }
.tt--night .tt-sky--night { opacity: 1; }

/* yildizlar */
.tt-stars { position: absolute; inset: 0; opacity: 0; transition: opacity 0.7s; }
.tt--night .tt-stars { opacity: 1; }
.tt-stars i { position: absolute; width: var(--s); height: var(--s); border-radius: 50%; background: #fff; box-shadow: 0 0 4px #fff; animation: ttTw 2.6s ease-in-out infinite; animation-delay: var(--d); }
@keyframes ttTw { 0%, 100% { opacity: 0.25; transform: scale(0.7); } 50% { opacity: 1; transform: scale(1); } }

/* gunduz gunesi (sagda) */
.tt-sun {
  position: absolute; right: 14%; top: 50%; width: 22px; height: 22px; margin-top: -11px; border-radius: 50%;
  background: radial-gradient(circle at 35% 35%, #fff6b0, #ffd23f 55%, #f5b301);
  box-shadow: 0 0 10px 3px rgba(255, 214, 64, 0.65), 0 0 22px 8px rgba(255, 214, 64, 0.25);
  animation: ttGlow 3.2s ease-in-out infinite;
  transition: transform 0.7s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.5s;
}
.tt--night .tt-sun { opacity: 0; transform: translateY(26px) scale(0.6); }
@keyframes ttGlow { 0%, 100% { box-shadow: 0 0 10px 3px rgba(255, 214, 64, 0.65), 0 0 22px 8px rgba(255, 214, 64, 0.25); } 50% { box-shadow: 0 0 14px 5px rgba(255, 214, 64, 0.8), 0 0 30px 12px rgba(255, 214, 64, 0.3); } }

/* gece hilali (solda) */
.tt-moon {
  position: absolute; left: 13%; top: 50%; width: 22px; height: 22px; margin-top: -11px; border-radius: 50%;
  box-shadow: inset -7px -3px 0 0 #ffd54a; filter: drop-shadow(0 0 5px rgba(255, 213, 74, 0.6));
  opacity: 0; transform: translateY(-26px) rotate(-30deg) scale(0.6);
  transition: transform 0.8s cubic-bezier(0.22, 1, 0.36, 1) 0.1s, opacity 0.5s 0.1s;
}
.tt--night .tt-moon { opacity: 1; transform: translateY(0) rotate(-20deg) scale(1); }

/* bulutlar */
.tt-clouds { position: absolute; left: 0; bottom: -3px; width: 100%; height: 66%; overflow: visible; }
.tt-clouds--back path { fill: rgba(255, 255, 255, 0.55); transition: fill 0.7s; }
.tt-clouds--front path { fill: #fff; transition: fill 0.7s; filter: drop-shadow(0 -1px 1px rgba(255, 255, 255, 0.6)); }
.tt--night .tt-clouds--back path { fill: rgba(150, 170, 255, 0.28); }
.tt--night .tt-clouds--front path { fill: rgba(200, 212, 255, 0.85); }
.tt-drift { animation: ttDrift 22s linear infinite; }
.tt-drift--fast { animation-duration: 14s; animation-direction: reverse; }
@keyframes ttDrift { from { transform: translateX(0); } to { transform: translateX(-240px); } }

/* top */
.tt-knob {
  position: absolute; z-index: 3; top: 50%; left: 5px; width: var(--k); height: var(--k); margin-top: calc(var(--k) / -2); border-radius: 50%;
  background: radial-gradient(circle at 35% 30%, #ffffff, #f3f7ff 60%, #dfe8f7);
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.25), 0 0 0 0 rgba(255, 255, 255, 0);
  transition: left 0.7s cubic-bezier(0.68, -0.25, 0.27, 1.25), box-shadow 0.7s, background 0.7s;
}
.tt--night .tt-knob {
  left: calc(100% - var(--k) - 5px);
  background: radial-gradient(circle at 38% 32%, #ffffff, #eef1ff 55%, #cfd6f3);
  box-shadow: 0 0 12px 4px rgba(255, 255, 255, 0.55), 0 0 30px 10px rgba(170, 180, 255, 0.35);
}
.tt-craters { position: absolute; inset: 0; opacity: 0; transition: opacity 0.6s 0.2s; }
.tt--night .tt-craters { opacity: 1; }
.tt-craters i { position: absolute; border-radius: 50%; background: rgba(160, 170, 210, 0.35); }
.tt-craters i:nth-child(1) { width: 26%; height: 26%; left: 22%; top: 28%; }
.tt-craters i:nth-child(2) { width: 16%; height: 16%; left: 58%; top: 52%; }
.tt-craters i:nth-child(3) { width: 12%; height: 12%; left: 40%; top: 66%; }
.tt:hover .tt-knob { transform: scale(1.04); }
.tt:active .tt-knob { transform: scale(0.96); }
.tt-knob { transform-origin: center; transition: left 0.7s cubic-bezier(0.68, -0.25, 0.27, 1.25), box-shadow 0.7s, background 0.7s, transform 0.2s; }

/* daraltilmis menu: kucuk dugme */
.tt--mini { --k: 18px; height: 26px; }
.tt--mini .tt-sun, .tt--mini .tt-moon { width: 10px; height: 10px; margin-top: -5px; }
.tt--mini .tt-moon { box-shadow: inset -3px -1px 0 0 #ffd54a; }
.tt--mini .tt-knob { left: 4px; }
.tt--mini.tt--night .tt-knob { left: calc(100% - var(--k) - 4px); }
.tt--mini .tt-stars { display: none; }

@media (prefers-reduced-motion: reduce) {
  .tt *, .tt { animation: none !important; transition: none !important; }
}
</style>
